import type { AdminModelsAnswer, SavedModel } from "./admin-models.js";
import type { AdminWorkspace, AdminWorkspacesAnswer, RestartAllAnswer } from "./admin-workspaces.js";
import { configuredHost } from "./host.js";
import { text } from "./i18n.js";
import { HOSTED_API } from "./text/login.js";
import { serverText } from "./text/server.js";

/** One window of the plan's included usage, in credits: how much is spent of the allowance, and when it starts over. */
export interface UsageWindow {
  limit: number;
  spent: number;
  resetAt: number;
}

export interface HostedSession {
  enabled: boolean;
  authenticated: boolean;
  account?: { id: string; username: string };
  state?: "starting" | "ready" | "stopped" | "stopping" | "resizing" | "error";
  tier?: "lite" | "heavy";
  tiers?: { id: "lite" | "heavy"; slots: number; mainModel: string }[];
  profile?: "lite" | "full";
  memoryMb?: number;
  error?: string | null;
  capacity?: { total: number; used: number };
  /** Set by the cloud app, which has no signup of its own: where to create an account. */
  signupUrl?: string | undefined;
  /** The local/self-hosted product is a demo running on the operator's own PC and says so.
   *  Only the cloud app sends this (false), so an absent value keeps the demo wording. */
  demo?: boolean | undefined;
  /**
   * The subscription's included usage, weekly and monthly (the platform's
   * usage windows). Only the cloud app has a plan to report; a window is null
   * when the plan sets no allowance for it.
   */
  usage?: { weekly: UsageWindow | null; monthly: UsageWindow | null };
  /**
   * Signed in, but the account may not have a workspace: no active
   * subscription, or an account suspended. Only the cloud app sends this, with
   * the platform's reason and the portal where plans are bought.
   */
  blocked?: { reason: string; portalUrl?: string | undefined };
  /**
   * The account is one of the operators, as the platform's list says. Only the
   * cloud app sends this; it opens the app's administration at /admin.
   */
  admin?: boolean | undefined;
}

async function request<T>(path: string, body?: unknown, method: "POST" | "PUT" = "POST"): Promise<T> {
  const response = await fetch(`${configuredHost() ?? ""}${path}`, {
    credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(10_000),
    ...(body == null ? {} : { method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
  });
  const value = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(value.error != null ? serverText(value.error) : text(HOSTED_API).requestFailed(response.status));
  return value;
}

export const hostedApi = {
  session: (): Promise<HostedSession> => request("/auth/session"),
  login: (username: string, password: string): Promise<HostedSession> => request("/auth/login", { username, password }),
  signup: (username: string, password: string, profile: "lite" | "full"): Promise<HostedSession> => request("/auth/signup", { username, password, profile }),
  logout: (): Promise<{ ok: boolean }> => request("/auth/logout", {}),
  start: (): Promise<HostedSession> => request("/api/workspace/start", {}),
  restart: (): Promise<HostedSession> => request("/api/workspace/restart", {}),
  /** The cloud app's administration (infra/app): every priced model beside the offer, and the offer replaced whole. */
  adminModels: (): Promise<AdminModelsAnswer> => request("/api/admin/models"),
  saveAdminModels: (models: readonly SavedModel[]): Promise<{ ok: boolean }> => request("/api/admin/models", { models }, "PUT"),
  /** Every workspace with its user; a restart backs one up and starts it again with the app's current settings, and restart-all does so to every running one. */
  adminWorkspaces: (): Promise<AdminWorkspacesAnswer> => request("/api/admin/workspaces"),
  restartWorkspace: (id: string): Promise<{ workspace: AdminWorkspace }> => request("/api/admin/workspaces/restart", { id }),
  restartAllWorkspaces: (): Promise<RestartAllAnswer> => request("/api/admin/workspaces/restart-all", {}),
};
